import { useQuery } from "@apollo/client/react";
import { MyAvatarDocument, MyselfDocument } from "@/graphql/generated/operations";
import { useAuthStore } from "@/stores/auth-store";

export interface Me {
  id?: string;
  fullName: string;
  firstName: string;
  email?: string;
  avatarContent?: string | null;
  avatarMime?: string | null;
}

// The current user. Name prefers the Person record (genesis `Myself`) over the
// Keycloak JWT, which is only a bootstrap fallback before Myself loads. Email is
// the opposite: always the JWT, never Person.email (the editable Visitenkarte
// contact address). Don't switch to Person.emailPrimary - its resolver can return
// null despite being non-null in the schema, which nukes the whole Myself query
// (broke /save/basics when tried). Both queries are deduped by Apollo.
export function useMe(): Me {
  const profile = useAuthStore((s) => s.profile);
  const authenticated = useAuthStore((s) => s.status === "authenticated");

  const { data: myselfData } = useQuery(MyselfDocument, { skip: !authenticated });
  const { data: avatarData } = useQuery(MyAvatarDocument, { skip: !authenticated });

  const person = myselfData?.myself;
  // `given` is a list of given names; `family` is a single string.
  const givenNames = (person?.name?.given ?? []).map((g) => g.trim()).filter(Boolean);
  const dbGivenFull = givenNames.join(" ");
  const dbGivenFirst = givenNames[0] ?? "";
  const dbFamily = person?.name?.family?.trim() ?? "";
  const hasDbName = Boolean(dbGivenFull || dbFamily);

  const jwtFull =
    [profile?.givenName, profile?.familyName].filter(Boolean).join(" ").trim() ||
    profile?.name?.trim() ||
    "";

  const fullName = hasDbName ? [dbGivenFull, dbFamily].filter(Boolean).join(" ") : jwtFull;
  const firstName =
    (hasDbName ? dbGivenFirst : profile?.givenName?.trim()) || fullName.split(/\s+/)[0] || "";

  return {
    id: person?.id,
    fullName,
    firstName,
    email: profile?.email ?? profile?.preferredUsername ?? person?.email ?? undefined,
    avatarContent: avatarData?.myAvatar?.content,
    avatarMime: avatarData?.myAvatar?.mimeType,
  };
}
